export class DeepChatHistoryStorageManager {
  constructor({ storageKey = "deep_chat_history_v1", maxSessions = 50 } = {}) {
    this.storageKey = storageKey;
    this.maxSessions = maxSessions;
  }

  createSession(initialTitle = "New deep chat") {
    const id =
      typeof crypto !== "undefined" && crypto?.randomUUID
        ? crypto.randomUUID()
        : `deep-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const now = new Date().toISOString();
    const next = {
      id,
      title: initialTitle,
      createdAt: now,
      updatedAt: now,
      messages: [],
    };

    const sessions = this._readSessions();
    sessions.unshift(next);
    this._writeSessionsWithEviction(sessions);
    return id;
  }

  listSessions() {
    const sessions = this._readSessions();
    return sessions.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }

  getSession(id) {
    if (!id) return null;
    const sessions = this._readSessions();
    return sessions.find((s) => s.id === id) || null;
  }

  upsertSession({ id, messages = [] }) {
    if (!id) return;
    const sessions = this._readSessions();
    const now = new Date().toISOString();
    const title = this._deriveTitle(messages);

    const existingIndex = sessions.findIndex((s) => s.id === id);
    if (existingIndex >= 0) {
      sessions[existingIndex] = {
        ...sessions[existingIndex],
        title: title || sessions[existingIndex].title || "New deep chat",
        messages: Array.isArray(messages) ? messages : [],
        updatedAt: now,
      };
    } else {
      sessions.unshift({
        id,
        title: title || "New deep chat",
        createdAt: now,
        updatedAt: now,
        messages: Array.isArray(messages) ? messages : [],
      });
    }

    this._writeSessionsWithEviction(sessions);
  }

  deleteSession(id) {
    if (!id) return;
    const sessions = this._readSessions().filter((s) => s.id !== id);
    this._writeSessionsWithEviction(sessions);
  }

  _deriveTitle(messages) {
    if (!Array.isArray(messages)) return "";
    const firstUser = messages.find(
      (m) => m && m.sender === "user" && typeof m.text === "string" && m.text.trim(),
    );
    if (!firstUser) return "";
    const cleaned = firstUser.text.trim();
    return cleaned.length > 60 ? `${cleaned.slice(0, 57)}...` : cleaned;
  }

  _readSessions() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  _writeSessionsWithEviction(sessions) {
    const normalized = Array.isArray(sessions) ? [...sessions] : [];
    const deduped = this._dedupeById(normalized).slice(0, this.maxSessions);

    // Sort oldest -> newest for deterministic eviction.
    deduped.sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());

    while (deduped.length >= 0) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(deduped));
        return;
      } catch (e) {
        // If localStorage is full, erase the oldest chat and retry.
        if (deduped.length === 0) return;
        deduped.shift();
      }
    }
  }

  _dedupeById(sessions) {
    const seen = new Set();
    const out = [];
    for (let i = sessions.length - 1; i >= 0; i--) {
      const current = sessions[i];
      if (!current?.id || seen.has(current.id)) continue;
      seen.add(current.id);
      out.unshift(current);
    }
    return out;
  }
}

